import type { Product } from '../../types';
import { useRef, useState, type TouchEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalizedEntityField, getNormalizedLanguageCode } from '../../i18n/localization';
import {
  getAvailableWeights,
  getDefaultWeight,
  getWeightLabel,
  getWeightPrice,
} from '../../utils/weight';
import Icon from '../../ui-kit/Icon';

interface ProductCardProps {
  product: Product;
  getCartQuantity: (weight: number) => number;
  onOpen: () => void;
  onAddToCart: (weight: number) => void;
  onRemoveFromCart: (weight: number) => void;
  onOpenManufacturer?: (manufacturerId: number) => void;
  formatCurrency: (value: number) => string;
}

export default function ProductCard({
  product,
  getCartQuantity,
  onOpen,
  onAddToCart,
  onRemoveFromCart,
  onOpenManufacturer,
  formatCurrency,
}: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const languageCode = getNormalizedLanguageCode(i18n.resolvedLanguage ?? i18n.language);
  const [imageSelection, setImageSelection] = useState({
    productId: product.id,
    index: 0,
  });
  const activeImageIndex =
    imageSelection.productId === product.id && imageSelection.index < product.images.length
      ? imageSelection.index
      : 0;
  const activeImage = product.images[activeImageIndex] ?? null;
  const hasMultipleImages = product.images.length > 1;
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const suppressOpenUntil = useRef(0);
  const manufacturer = product.manufacturer;
  const category = product.category;
  const productName = getLocalizedEntityField(product, 'name', languageCode);
  const manufacturerName = manufacturer
    ? getLocalizedEntityField(manufacturer, 'name', languageCode)
    : null;
  const categoryName = category ? getLocalizedEntityField(category, 'name', languageCode) : null;
  const availableWeights = getAvailableWeights(product);
  const defaultWeight = getDefaultWeight(product);
  const [weightSelection, setWeightSelection] = useState({
    productId: product.id,
    weight: defaultWeight,
  });
  const selectedWeight =
    weightSelection.productId === product.id && availableWeights.includes(weightSelection.weight)
      ? weightSelection.weight
      : defaultWeight;
  const showWeightSelector = product.pricingUnit !== 'PER_UNIT' && availableWeights.length > 1;

  const cartQuantity = getCartQuantity(selectedWeight);
  const displayPrice = getWeightPrice(product, selectedWeight);

  const showImage = (index: number) => {
    const imageCount = product.images.length;

    if (imageCount < 2) return;

    setImageSelection({
      productId: product.id,
      index: (index + imageCount) % imageCount,
    });
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];

    if (!touch) return;

    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    touchStart.current = null;

    if (!start || !touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;

    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

    suppressOpenUntil.current = Date.now() + 500;
    showImage(activeImageIndex + (deltaX < 0 ? 1 : -1));
  };

  return (
    <article
      className="flex flex-col gap-3 cursor-pointer group"
      onClick={() => {
        if (Date.now() < suppressOpenUntil.current) return;
        onOpen();
      }}
    >
      <div
        className="relative aspect-square touch-pan-y overflow-hidden rounded-[var(--radius-xl)] bg-secondary transition-shadow duration-200 hover:shadow-ambient"
        role={hasMultipleImages ? 'region' : undefined}
        aria-roledescription={hasMultipleImages ? t('productCard.carousel') : undefined}
        aria-label={
          hasMultipleImages ? t('productCard.carouselLabel', { name: productName }) : undefined
        }
        onTouchStart={hasMultipleImages ? handleTouchStart : undefined}
        onTouchEnd={hasMultipleImages ? handleTouchEnd : undefined}
        onTouchCancel={hasMultipleImages ? () => (touchStart.current = null) : undefined}
      >
        {activeImage ? (
          <img
            key={activeImage.id}
            src={activeImage.url}
            alt={
              hasMultipleImages
                ? t('productCard.imageAlt', {
                    name: productName,
                    current: activeImageIndex + 1,
                    total: product.images.length,
                  })
                : productName
            }
            className="h-full w-full select-none object-cover transition-transform duration-200 motion-reduce:transition-none group-hover:scale-[1.02] motion-reduce:group-hover:scale-100"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-secondary text-text-secondary text-sm">
            <span>{t('common.noImage')}</span>
          </div>
        )}
        {hasMultipleImages ? (
          <>
            <button
              type="button"
              className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] border border-border bg-carousel-control text-text shadow-ambient transition-colors hover:bg-carousel-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:bg-surface-high motion-reduce:transition-none"
              onClick={(event) => {
                event.stopPropagation();
                showImage(activeImageIndex - 1);
              }}
              aria-label={t('productCard.previousImage')}
            >
              <Icon name="chevron_left" className="text-[22px] !leading-none" />
            </button>
            <button
              type="button"
              className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] border border-border bg-carousel-control text-text shadow-ambient transition-colors hover:bg-carousel-control-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent active:bg-surface-high motion-reduce:transition-none"
              onClick={(event) => {
                event.stopPropagation();
                showImage(activeImageIndex + 1);
              }}
              aria-label={t('productCard.nextImage')}
            >
              <Icon name="chevron_right" className="text-[22px] !leading-none" />
            </button>
            <span
              className="absolute right-2 top-2 z-10 rounded-[var(--radius-full)] border border-border bg-primary px-2 py-1 text-[0.6875rem] font-semibold leading-none text-text shadow-sm"
              aria-live="polite"
            >
              {t('productCard.imagePosition', {
                current: activeImageIndex + 1,
                total: product.images.length,
              })}
            </span>
          </>
        ) : null}
        {showWeightSelector ? (
          <div className="absolute bottom-3 left-3 z-10 max-w-[calc(100%-84px)]">
            <div className="flex flex-col items-start gap-1.5">
              {availableWeights.map((weight) => {
                const isSelected = weight === selectedWeight;
                return (
                  <button
                    key={weight}
                    type="button"
                    className={`rounded-[var(--radius-full)] border px-3 py-1.5 text-[0.6875rem] font-semibold leading-none backdrop-blur-sm transition-colors ${
                      isSelected
                        ? 'border-accent bg-accent text-on-accent'
                        : 'border-primary bg-primary text-text'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setWeightSelection({
                        productId: product.id,
                        weight,
                      });
                    }}
                    aria-pressed={isSelected}
                    aria-label={t('product.weightLabel', {
                      weight: getWeightLabel(product, weight, languageCode),
                    })}
                  >
                    {getWeightLabel(product, weight, languageCode)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="absolute bottom-3 right-3 z-10">
          {cartQuantity > 0 ? (
            <div className="inline-flex items-center gap-2">
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-full)] bg-accent-gradient text-on-accent shadow-lg border-none cursor-pointer transition-transform active:scale-90"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFromCart(selectedWeight);
                }}
                aria-label={t('productCard.removeFromCartAria')}
              >
                <svg
                  className="absolute"
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  viewBox="0 0 24 24"
                  width="20"
                  fill="currentColor"
                >
                  <path d="M19 13H5v-2h14v2z" />
                </svg>
              </button>
              <button
                type="button"
                className="flex h-10 min-w-[40px] px-3 items-center justify-center rounded-[var(--radius-full)] bg-accent-gradient text-on-accent shadow-lg border-none cursor-pointer transition-transform active:scale-90 text-[0.9375rem] font-bold leading-none"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToCart(selectedWeight);
                }}
                aria-label={t('productCard.addToCartWithCountAria', { count: cartQuantity })}
              >
                {cartQuantity}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-full)] bg-accent-gradient text-on-accent shadow-lg border-none cursor-pointer transition-transform active:scale-90"
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(selectedWeight);
              }}
              aria-label={t('productCard.addToCartAria')}
            >
              <svg
                className="absolute"
                xmlns="http://www.w3.org/2000/svg"
                height="20"
                viewBox="0 0 24 24"
                width="20"
                fill="currentColor"
              >
                <path d="M11 13H5v-2h6V5h2v6h6v2h-6v6h-2v-6z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-text-secondary opacity-70">
          {categoryName || t('productCard.defaultCategory')}
        </span>
        <h3 className="font-semibold text-text leading-tight m-0 text-base">{productName}</h3>
        {manufacturer && onOpenManufacturer ? (
          <a
            role="link"
            tabIndex={0}
            className="text-accent text-[0.8125rem] font-semibold underline underline-offset-2 decoration-accent/30 hover:decoration-accent active:opacity-60 transition-all cursor-pointer w-max"
            onClick={(e) => {
              e.stopPropagation();
              onOpenManufacturer(manufacturer.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onOpenManufacturer(manufacturer.id);
              }
            }}
          >
            {manufacturerName}
          </a>
        ) : null}
        <p className="font-bold text-tertiary m-0">{formatCurrency(displayPrice)}</p>
      </div>
    </article>
  );
}
